import type { SearchEngine } from '../credentials';
import type { SearchDescriptor } from '../descriptor';
import type { Predicate } from '../predicate';
import { and, eq, eqOpt, inList, neq, or } from '../predicate';

export type OrderSearchType = 'PurchaseOrder' | 'SalesOrder' | 'BuySellOrder' | 'WorkOrder';

export const TYPESENSE_ORDER_TYPE: Readonly<Record<OrderSearchType, string>> = {
  SalesOrder: 'SALES_ORDER',
  PurchaseOrder: 'PURCHASE_ORDER',
  WorkOrder: 'WORK_ORDER',
  BuySellOrder: 'BUYSELL_ORDER',
};

export type OrderSearchStatus =
  | 'DRAFT_CUSTOMER'
  | 'DRAFT'
  | 'PENDING'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'RECEIVING'
  | 'PICKING'
  | 'SHIPPED'
  | 'PAYMENT_RECONCILIATION'
  | 'PENDING_LIQUIDATION'
  | 'PENDING_SETTLEMENT'
  | 'CLOSED'
  | 'CANCELLED'
  | 'ERROR';

export interface OrderCustomField {
  readonly key: string;
  readonly value: unknown;
}

export interface OrderDoc {
  readonly objectID: string;
  readonly orderId: number;
  readonly organizationId: number;
  readonly __type: OrderSearchType;
  readonly orderType?: string;
  readonly slug: string;
  readonly slugs: readonly string[];
  readonly status: OrderSearchStatus;
  readonly isTemplate: boolean;
  readonly customerPO?: string;
  readonly customerPOs?: readonly string[];
  readonly salesPerson?: string;
  readonly buyer?: string;
  readonly buyerName?: string;
  readonly vendorSO?: string;
  readonly vendorSOs?: readonly string[];
  readonly vendor?: string;
  readonly customer?: string;
  readonly broker?: string;
  readonly subsidiary?: string;
  readonly subsidiaryName?: string;
  readonly shippedFromName?: string;
  readonly shipToName?: string;
  readonly edi?: boolean;
  readonly soldThroughSalesOrderId?: number | null;
  readonly sourceId?: number;
  readonly counterpartyName?: string;
  readonly customFields?: readonly OrderCustomField[];
}

export const ORDER_DESCRIPTOR: SearchDescriptor<OrderDoc> = {
  entity: 'order',
  indexName: { algolia: 'orders', typesense: 'orders' },
  searchable: [
    '__type',
    'slug',
    'slugs',
    'customerPO',
    'customerPOs',
    'salesPerson',
    'buyer',
    'status',
    'vendorSO',
    'vendorSOs',
    'vendor',
    'customer',
    'broker',
    'subsidiary',
    'subsidiaryName',
    'shippedFromName',
    'customFields',
  ],
  filterable: ['isTemplate', 'organizationId', 'edi', 'status', '__type', 'soldThroughSalesOrderId', 'orderType'],
  defaults: { hitsPerPage: 20 },
  algolia: {
    searchableAttributes: [
      { path: ['__type'] },
      { path: ['slug'] },
      { path: ['slugs'] },
      { path: ['customerPO'] },
      { path: ['customerPOs'] },
      { path: ['salesPerson'] },
      { path: ['buyer'] },
      { path: ['status'] },
      { path: ['vendorSO'] },
      { path: ['vendorSOs'] },
      { path: ['vendor'] },
      { path: ['customer'] },
      { path: ['broker'] },
      { path: ['subsidiary'] },
      { path: ['subsidiaryName'] },
      { path: ['shippedFromName'] },
      { path: ['customFields', 'value'], mode: 'unordered' },
    ],
    attributesForFaceting: [
      { path: ['isTemplate'] },
      { path: ['organizationId'], mode: 'filterOnly' },
      { path: ['edi'], mode: 'filterOnly' },
      { path: ['status'], mode: 'searchable' },
      { path: ['__type'], mode: 'searchable' },
    ],
  },
  ranking: { typesense: { queryBy: 'slugTokens,slug,counterpartyName,reference,lineItemsText,notesText' } },
  tenantField: 'organizationId',
};

export const ORDER_LINKABLE_TO_SO = (): Predicate<OrderDoc> => inList('status', ['DRAFT', 'SUBMITTED']);

export const ORDER_AT_DOCK = (): Predicate<OrderDoc> => inList('status', ['RECEIVING', 'PICKING']);

export const ORDER_ATTACHABLE_TO_BILL = (engine: SearchEngine = 'algolia'): Predicate<OrderDoc> =>
  engine === 'typesense'
    ? neq('status', 'CLOSED')
    : and([
        neq('status', 'CLOSED'),
        // Legacy wire compatibility only: Algolia cannot filter actual null or missing attributes.
        or([eq('isTemplate', false), eq<OrderDoc>('isTemplate', 'null')]),
      ]);

/** Algolia stores the type as `__type`; the Typesense writer stores it as `orderType`. */
export const ORDER_OF_TYPE = (engine: SearchEngine, type: OrderSearchType): Predicate<OrderDoc> =>
  engine === 'typesense' ? eq('orderType', TYPESENSE_ORDER_TYPE[type]) : eq('__type', type);

export const PO_SOLD_THROUGH = (
  salesOrderId: number | undefined,
  engine: SearchEngine = 'algolia'
): Predicate<OrderDoc> =>
  and([ORDER_OF_TYPE(engine, 'PurchaseOrder'), eqOpt('soldThroughSalesOrderId', salesOrderId), ORDER_LINKABLE_TO_SO()]);

export interface OrderHit {
  readonly orderId: number;
  readonly type: OrderSearchType;
  readonly slug: string;
  readonly status: string;
  readonly buyer?: string;
  readonly vendor?: string;
  readonly counterparty?: string;
  readonly customerPO?: string;
  readonly vendorSO?: string;
  readonly shipToName?: string;
}

const ORDER_SEARCH_TYPE_BY_TYPESENSE: Readonly<Record<string, OrderSearchType>> = Object.fromEntries(
  Object.entries(TYPESENSE_ORDER_TYPE).map(([type, value]) => [value, type as OrderSearchType])
);

const ORDER_SEARCH_TYPES: ReadonlySet<string> = new Set(Object.keys(TYPESENSE_ORDER_TYPE));

export function toOrderHit(doc: OrderDoc): OrderHit | null {
  const orderId = doc.orderId ?? doc.sourceId;
  const type =
    doc.__type !== undefined && ORDER_SEARCH_TYPES.has(doc.__type)
      ? doc.__type
      : ORDER_SEARCH_TYPE_BY_TYPESENSE[doc.orderType ?? ''];
  if (typeof orderId !== 'number' || type === undefined) return null;

  const buyer = doc.buyer ?? doc.buyerName;
  const vendor = doc.vendor ?? (type === 'PurchaseOrder' ? doc.counterpartyName : undefined);
  const counterparty = doc.counterpartyName ?? doc.buyer ?? doc.vendor ?? doc.customer;
  return {
    orderId,
    type,
    slug: doc.slug,
    status: doc.status,
    ...(buyer === undefined ? {} : { buyer }),
    ...(vendor === undefined ? {} : { vendor }),
    ...(counterparty === undefined ? {} : { counterparty }),
    ...(doc.customerPO === undefined ? {} : { customerPO: doc.customerPO }),
    ...(doc.vendorSO === undefined ? {} : { vendorSO: doc.vendorSO }),
    ...(doc.shipToName === undefined ? {} : { shipToName: doc.shipToName }),
  };
}
